import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { formatDateTime } from '@/shared/lib'
import {
  CopyButton,
  ErrorState,
  KeyValue,
  SectionHeader,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Skeleton,
  StatusBadge,
} from '@/shared/ui'

import { leadQuery } from '../api/queries'
import { leadTypeTone } from '../model/lead-badge'
import type { LeadDetail } from '../model/types'

type LeadDetailSheetProps = {
  leadId: string | undefined
  onClose: () => void
}

export function LeadDetailSheet({ leadId, onClose }: LeadDetailSheetProps) {
  return (
    <Sheet open={leadId !== undefined} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-drawer max-w-full"
        /*
         * Where the focus goes when the panel closes.
         *
         * Base UI hands it back to the trigger, and this panel has none: it is
         * opened by a click on a table row and kept open by the URL, so the
         * default left focus on `<body>` and a keyboard reader started the
         * table again from the top after every lead they looked at.
         *
         * The row is found by the id it was drawn with rather than held in a
         * ref, because the panel outlives the row — reload the page with a lead
         * open and the table renders after the panel does.
         */
        finalFocus={() =>
          leadId === undefined
            ? null
            : document.querySelector<HTMLElement>(`[data-row-id="${leadId}"]`)
        }
      >
        {leadId !== undefined && <LeadDetailBody leadId={leadId} />}
      </SheetContent>
    </Sheet>
  )
}

function LeadDetailBody({ leadId }: { leadId: string }) {
  const { data: lead, isPending, error, refetch } = useQuery(leadQuery(leadId))

  if (error !== null) {
    return (
      <div className="p-6">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    )
  }

  if (isPending || lead === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  return <LeadDetailContent lead={lead} />
}

/** A value or an em dash — empty strings must not read as a filled field. */
const orDash = (value: string) => (value === '' ? '—' : value)

function LeadDetailContent({ lead }: { lead: LeadDetail }) {
  const { t } = useTranslation(['leads', 'common'])

  // The gutter is reserved, not discovered: this pane swaps a loading state for
  // a record of unknown length, and a scrollbar arriving with the record would
  // narrow every row in it by 10px at the same moment.
  return (
    <div className="flex h-full flex-col overflow-y-auto [scrollbar-gutter:stable]">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          {t('detail.title')}
          <StatusBadge tone={leadTypeTone(lead.type)} size="sm">
            {t(`type.${lead.type}`, { defaultValue: lead.type })}
          </StatusBadge>
        </SheetTitle>
        <SheetDescription className="font-mono">{formatDateTime(lead.createdAt)}</SheetDescription>
      </SheetHeader>

      <div className="flex flex-col gap-6 px-6 pb-8">
        <section className="flex flex-col gap-4">
          <SectionHeader>{t('detail.groups.classification')}</SectionHeader>
          <div className="grid grid-cols-2 gap-4">
            <KeyValue label={t('detail.fields.blockReason')}>
              {lead.blockReason === '' || lead.blockReason === 'none'
                ? '—'
                : t(`blockReason.${lead.blockReason}`, { defaultValue: lead.blockReason })}
            </KeyValue>
            <KeyValue label={t('detail.fields.page')}>
              {t(`page.${lead.page}`, { defaultValue: orDash(lead.page) })}
            </KeyValue>
            <KeyValue label={t('detail.fields.domain')} isMono>
              {orDash(lead.domain)}
            </KeyValue>
            <KeyValue label={t('detail.fields.source')}>
              {t(`common:source.${lead.source}`, { defaultValue: orDash(lead.source) })}
            </KeyValue>
          </div>
          <KeyValue label={t('detail.fields.uri')} isMono>
            {orDash(lead.uri)}
          </KeyValue>
          {/* On the global list nothing else says which campaign this hit
              belongs to — the backend sends the id, so it becomes the way in. */}
          <KeyValue label={t('detail.fields.campaign')} isMono>
            <Link
              to="/campaigns/$campaignId"
              params={{ campaignId: lead.campaignId }}
              search={{ view: 'settings' as const, page: 0, leadId: undefined }}
              className="text-accent-text hover:underline"
            >
              {lead.campaignId}
            </Link>
          </KeyValue>
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeader>{t('detail.groups.geo')}</SectionHeader>
          <div className="grid grid-cols-3 gap-4">
            <KeyValue label={t('detail.fields.country')} isMono>
              {orDash(lead.country)}
            </KeyValue>
            <KeyValue label={t('detail.fields.city')}>{orDash(lead.city)}</KeyValue>
            <KeyValue label={t('detail.fields.lang')} isMono>
              {orDash(lead.lang)}
            </KeyValue>
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeader>{t('detail.groups.device')}</SectionHeader>
          <div className="grid grid-cols-2 gap-4">
            <KeyValue label={t('detail.fields.deviceType')}>
              {t(`common:device.${lead.deviceType}`, { defaultValue: orDash(lead.deviceType) })}
            </KeyValue>
            <KeyValue label={t('detail.fields.deviceOs')}>
              {t(`common:os.${lead.deviceOs}`, { defaultValue: orDash(lead.deviceOs) })}
            </KeyValue>
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeader>{t('detail.groups.network')}</SectionHeader>
          <div className="grid grid-cols-2 gap-4">
            <KeyValue label={t('detail.fields.ip')} isMono>
              <span className="flex items-center gap-1.5">
                {orDash(lead.ip)}
                {lead.ip !== '' && <CopyButton value={lead.ip} label={t('common:actions.copy')} />}
              </span>
            </KeyValue>
            <KeyValue label={t('detail.fields.isp')}>{orDash(lead.isp)}</KeyValue>
            <KeyValue label={t('detail.fields.isVpn')}>
              {lead.isVpn ? t('detail.yes') : t('detail.no')}
            </KeyValue>
            <KeyValue label={t('detail.fields.dns')} isMono>
              {orDash(lead.dns)}
            </KeyValue>
            <KeyValue label={t('detail.fields.xForwardedFor')} isMono>
              {orDash(lead.xForwardedFor)}
            </KeyValue>
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeader>{t('detail.groups.headers')}</SectionHeader>
          <KeyValue label={t('detail.fields.userAgent')} isMono>
            <span className="flex items-start gap-1.5">
              <span className="break-all">{orDash(lead.userAgent)}</span>
              {lead.userAgent !== '' && (
                <CopyButton value={lead.userAgent} label={t('common:actions.copy')} />
              )}
            </span>
          </KeyValue>
          <div className="grid grid-cols-2 gap-4">
            <KeyValue label={t('detail.fields.referer')} isMono>
              {orDash(lead.referer)}
            </KeyValue>
            <KeyValue label={t('detail.fields.acceptLanguage')} isMono>
              {orDash(lead.acceptLanguage)}
            </KeyValue>
            <KeyValue label={t('detail.fields.secChUaPlatform')} isMono>
              {orDash(lead.secChUaPlatform)}
            </KeyValue>
            <KeyValue label={t('detail.fields.secFetchSite')} isMono>
              {orDash(lead.secFetchSite)}
            </KeyValue>
            <KeyValue label={t('detail.fields.secChUa')} isMono>
              {orDash(lead.secChUa)}
            </KeyValue>
            <KeyValue label={t('detail.fields.secChUaMobile')} isMono>
              {orDash(lead.secChUaMobile)}
            </KeyValue>
            <KeyValue label={t('detail.fields.secFetchMode')} isMono>
              {orDash(lead.secFetchMode)}
            </KeyValue>
            <KeyValue label={t('detail.fields.secFetchDest')} isMono>
              {orDash(lead.secFetchDest)}
            </KeyValue>
          </div>
          {lead.utms.length > 0 && (
            <KeyValue label={t('detail.fields.utms')} isMono>
              {lead.utms.join(', ')}
            </KeyValue>
          )}
        </section>
      </div>
    </div>
  )
}
